import React from "react";

import { AdminSection, Button, Col, Form, Icon, Input, InputNumber, Option, Row, Select, Typography } from "components";

export const MethodPqTab = ({
  addQuestion,
  deleteQuestion,
  method,
  moveQuestionDown,
  moveQuestionUp,
  questions,
  setSq,
  sq,
  updatePlaceholder,
  updateQuantityQuestionPlaceholder,
  updateQuestion,
  updateQuestionOptional,
  updateQuestionOptions,
  updateQuestionSf,
  updateQuestionType,
}) => (
  <>
    <AdminSection
      headline="Quantity points"
      description={
        <>
          Modify the quantity points (P<sub>q</sub>) for all users by applying an S<sub>q</sub> factor. If S<sub>q</sub> is zero, this section will be ignored.
          Specify a minimum qty value, a maximum qty value, and a step (for example, if the step is 2, then users will only be allowed to enter multiples of 2).
        </>
      }
    />
    <Row>
      <Col sm={4} md={4}>
        <InputNumber
          name="sq"
          label="Sq"
          rules={[{ required: true, type: "number" }]}
          initialValue={method ? method.sq : undefined}
          className="no-suffix"
          onBlur={(v) => setSq(v.target.value)}
          min={0}
        />
      </Col>
      <Col sm={4} md={4}>
        <InputNumber
          name="quantityMin"
          label="Min qty"
          rules={[{ required: sq > 0, type: "number" }]}
          initialValue={method ? method.quantityMin : undefined}
          className="no-suffix"
          min={0}
        />
      </Col>
      <Col sm={4} md={4}>
        <InputNumber
          name="quantityMax"
          label="Max qty"
          rules={[{ required: sq > 0, type: "number" }]}
          initialValue={method ? method.quantityMax : undefined}
          className="no-suffix"
          min={0}
        />
      </Col>
      <Col sm={4} md={4}>
        <InputNumber
          name="quantityStep"
          label="Qty step"
          rules={[{ required: sq > 0, type: "number" }]}
          initialValue={method ? method.quantityStep : undefined}
          className="no-suffix"
          min={0}
          step={0.01}
        />
      </Col>
    </Row>
    <Row>
      <Col>
        <Input
          name={"quantityQuestion"}
          label={"Question for quantity input"}
          rules={[{ required: sq > 0 }]}
          initialValue={method ? method.quantityQuestion : undefined}
        />
      </Col>
    </Row>
    <Row>
      <Col>
        <Input
          name={"quantityQuestionPlaceholder"}
          label={"Placeholder for quantity input"}
          rules={[{ required: false }]}
          initialValue={method && method.quantityQuestionPlaceholder ? method.quantityQuestionPlaceholder : undefined}
          onChange={(e) => updateQuantityQuestionPlaceholder(e)}
        />
      </Col>
    </Row>

    <AdminSection
      headline="Quantity questions"
      description={
        <>
          Add any number of questions for users when adding a method. Important: S<sub>f</sub> is only applied for select and multi-select fields. Placeholder
          is only used for input, select and date fields (if you exclude a placeholder, then the default will be used).
        </>
      }
    />
    {questions.map((item, i) => (
      <div key={i}>
        {questions[i] && item.section === "quantity" && (
          <div className="margin-bottom-200">
            <Row>
              <Col sm={12} md={12}>
                <Typography variant="paragraph-m">
                  <b>Question {questions[i].count}</b>
                </Typography>
              </Col>
              <Col sm={12} md={12} className="right">
                <Icon iconName="tabler/trash" className="icon-btn" onClick={() => deleteQuestion(i)} />
                {questions[i].count > 1 && <Icon iconName="tabler/arrow-up" className="icon-btn" onClick={() => moveQuestionUp(i)} />}
                {questions[i].count < questions[questions.length - 1].count && questions[questions.length - 1].count > 1 && (
                  <Icon iconName="tabler/arrow-down" className="icon-btn" onClick={() => moveQuestionDown(i)} />
                )}
              </Col>
            </Row>
            <Row>
              <Col>
                <Input
                  name={`question-${i}`}
                  label={"Question"}
                  rules={[{ required: true }]}
                  initialValue={questions[i].question}
                  onBlur={(v) => updateQuestion(i, v)}
                />
              </Col>
            </Row>
            <Row>
              <Col sm={18} md={18}>
                <Input
                  name={`placeholder-${i}`}
                  label={"Placeholder"}
                  onBlur={(v) => updatePlaceholder(i, v)}
                  rules={[{ required: false }]}
                  initialValue={questions[i].placeholder}
                />
              </Col>
              <Col sm={6} md={6}>
                <Select
                  name={`optional-${i}`}
                  label="Optional"
                  rules={[{ required: true }]}
                  initialValue={questions[i].optional}
                  onChange={(v) => updateQuestionOptional(i, v)}
                >
                  <Option value={false}>Required</Option>
                  <Option value={true}>Not required</Option>
                </Select>
              </Col>
            </Row>
            <Row>
              <Col sm={8} md={8}>
                <Select
                  name={`type-${i}`}
                  label="Type"
                  rules={[{ required: true }]}
                  initialValue={questions[i].type}
                  onChange={(v) => updateQuestionType(i, v)}
                >
                  <Option value="input">Input</Option>
                  <Option value="number">Number input</Option>
                  <Option value="select">Select</Option>
                  <Option value="multi">Multi select</Option>
                  <Option value="date">Date</Option>
                  <Option value="image">Image</Option>
                </Select>
              </Col>
              {(questions[i].type === "select" || questions[i].type === "multi") && (
                <Col sm={16} md={16}>
                  <Select
                    name={`options-${i}`}
                    label="Enter options"
                    rules={[{ required: true }]}
                    initialValue={questions[i].values}
                    mode="tags"
                    style={{ width: "100%" }}
                    onChange={(v) => updateQuestionOptions(i, v)}
                  >
                    <Option value="Yes">Yes</Option>
                    <Option value="No">No</Option>
                  </Select>
                </Col>
              )}
            </Row>
            {questions[i].type === "select" && (
              <>
                {questions[i].values.map((item, k) => (
                  <Row key={k}>
                    <Col sm={8} md={8}>
                      <InputNumber
                        name={`sf-${i}-${k}`}
                        label="Sf"
                        onBlur={(v) => updateQuestionSf(i, k, v)}
                        min={0}
                        rules={[{ required: true, type: "number" }]}
                        className="no-suffix"
                        initialValue={questions[i].sf[k] ? parseFloat(questions[i].sf[k]) : undefined}
                      />
                    </Col>
                    <Col sm={16} md={16}>
                      <Typography variant="paragraph-m" style={{ margin: "1rem 0 0 0" }}>
                        For option <b>{item}</b>
                      </Typography>
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    ))}
    <Row>
      <Col>
        <Form.Item>
          <Button onClick={() => addQuestion("quantity")}>Add a question</Button>
        </Form.Item>
      </Col>
    </Row>
  </>
);
